import { IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { checkEs6AndRun, DialogAlert, useI18n } from 'AurionCR/components';
import {
  ErrorFileUploader,
  ErrorFileUploaderTypes,
  FileUploaderProps,
  FileUploaderRenderProps,
  Input,
} from 'AurionCR/components/formV2';
import { useOpen } from 'AurionCR/components/hooks';
import filetype from 'magic-bytes.js';
import mime from 'mime-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { API_MEDIA_UPLOADS } from '../../services/media-uploads';
import { createFileFormData, sanitizeFileName } from '../helpers.ts';

export const InputUploader: React.FC<FileUploaderRenderProps & { validationError: any }> = ({
  value,
  open,
  remove,
  error,
  renderProps = {},
  fileName,
  validationError,
}) => {
  const { label, disabled } = renderProps;
  const { isOpen, handleClose, handleOpen } = useOpen();
  useEffect(() => {
    if (error) {
      handleOpen();
    }
  }, [error, handleOpen]);

  return (
    <>
      <Input
        value={fileName}
        label={label}
        error={validationError || error?.message}
        onClear={remove}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton
              color="primary"
              component="span"
              size="small"
              disabled={disabled}
              onClick={open}
            >
              <CloudUploadIcon />
            </IconButton>
          ),
        }}
      />
      {value && fileName === value && (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      )}
      {isOpen && <DialogAlert message={error?.message} onClose={handleClose} />}
    </>
  );
};

export const FileUploader: React.FC<
  FileUploaderProps & { fileNamePrefixTemplate?: string; error?: any }
> = ({
  name,
  value,
  fileAccept = '.pdf,.doc,.docx,.xls,.xlsx,.htm,.html,application/msword,text/html,application/pdf,application/excel',
  apiSet = API_MEDIA_UPLOADS.UPLOAD_FILE_TO_CLOUD,
  apiRemove = API_MEDIA_UPLOADS.REMOVE_FILE_FROM_CLOUD,
  filePath: _filePath = 'Documents',
  onChange,
  fileMaxSize,
  render = InputUploader,
  renderProps,
  className = 'FormControllerStyled',
  fileNamePrefixTemplate = `\${data.id}`,
  error: validationError,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<any>({ FileName: '', Remove: null });

  const value_ = useMemo(
    () => (typeof value === 'string' ? value : state.FileName),
    [value, state.FileName],
  );

  const accept = useMemo<{ input?: string; types: null | string[]; exts: null | string[] }>(() => {
    if (fileAccept) {
      const items = fileAccept
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean);

      const exts_ = items
        .map((item) => (item.startsWith('.') ? item.toLowerCase() : null))
        .filter((item): item is string => Boolean(item));

      const types_ = items.reduce((result: string[], item: string) => {
        const type_ = mime.lookup(item);
        if (type_) {
          result.push(String(type_).toLowerCase());
        }
        if (item.includes('/')) {
          result.push(item.toLowerCase());
        }
        return result;
      }, []);

      if (types_.length || exts_.length)
        return {
          // Keep original accept string to avoid dropping extensions like ".eml".
          input: items.join(','),
          types: Array.from(new Set(types_)),
          exts: Array.from(new Set(exts_)),
        };
    }
    return {
      input: undefined,
      types: null,
      exts: null,
    };
  }, [fileAccept]);

  const { t } = useI18n();
  const onChangeData = useCallback(
    (base64 = '', fileName = '') => {
      let value: any = '';
      const { Remove, FileName } = state;
      const remove =
        !FileName && value_
          ? () => ({
              method: 'put',
              url: apiRemove,
              data: { filePath: value_ },
            })
          : Remove;

      if (remove || base64) {
        value = {
          mixin_: {
            requests: [],
            updateModel: 'filePath',
            name,
          },
          value: fileName,
        };
        if (remove) {
          value.mixin_.requests.push(remove);
        }
        if (base64) {
          value.mixin_.requests.push((data: any) => ({
            method: 'post',
            url: apiSet,
            data: createFileFormData(
              base64,
              sanitizeFileName(`${checkEs6AndRun(fileNamePrefixTemplate, data)}_${fileName}`),
            ),
          }));
        }
      }
      setState({ FileName: fileName, remove });
      if (!value?.mixin_?.requests.length) {
        onChange('');
      } else {
        onChange({ target: { value } });
      }
    },
    [state, setState, name, onChange, fileNamePrefixTemplate, apiRemove, apiSet, value_],
  );

  const [error, setError] = useState<ErrorFileUploader | null>(null);
  const errorController = useCallback(
    (type: ErrorFileUploaderTypes | null) => {
      if (type !== null && ref.current) {
        ref.current.value = '';
      }
      switch (type) {
        case null:
          setError(null);
          break;
        case 'ERROR_LOAD':
          setError(new ErrorFileUploader(type, t('file-error-load')));
          break;
        case 'INCORRECT_TYPE':
          setError(new ErrorFileUploader(type, t('file-incorrect-type')));
          break;
        case 'MAX_FILE_SIZE':
          setError(new ErrorFileUploader(type, `${t('file-max-size')} < ${fileMaxSize}Mb`));
          break;
        default:
          setError(new ErrorFileUploader('UNKNOWN', t('file-incorrect-type')));
      }
    },
    [t, fileMaxSize, ref],
  );

  const onLoadFile = useCallback(
    (file_: File) => {
      errorController(null);
      const fileReader = new FileReader();
      fileReader.onload = (e: any) => {
        if (e) {
          onChangeData(e.target.result, file_.name);
          if (ref.current) ref.current.value = '';
        } else {
          errorController('ERROR_LOAD');
        }
      };
      fileReader.readAsDataURL(file_);
    },
    [errorController, onChangeData, ref],
  );

  const onChangeFile = useCallback(
    async (e: any) => {
      errorController(null);
      const file_ = e.target.files[0];
      if (file_) {
        const fileType = file_.type?.toLowerCase();
        const fileExt = file_.name?.includes('.')
          ? `.${file_.name.split('.').pop()?.toLowerCase()}`
          : '';
        const isAcceptedByMime = Boolean(fileType && accept.types?.includes(fileType));
        const isAcceptedByExt = Boolean(fileExt && accept.exts?.includes(fileExt));
        const isAcceptedByDeclaredType = isAcceptedByMime || isAcceptedByExt;

        if (
          accept.types &&
          fileType &&
          !accept.types.some((type) => type === fileType) &&
          !isAcceptedByExt
        ) {
          errorController('INCORRECT_TYPE');
        } else if (fileMaxSize && file_.size > fileMaxSize * 1000000) {
          errorController('MAX_FILE_SIZE');
        } else if (accept.types) {
          try {
            const result = filetype(new Uint8Array(await file_.arrayBuffer()));
            if (!result.length) {
              // Some text-like formats (for example .eml/.msg/html/txt) may have no magic signature.
              // In this case trust the declared accept rule (mime/extension).
              if (isAcceptedByDeclaredType) {
                onLoadFile(file_);
              } else {
                errorController('INCORRECT_TYPE');
              }
              return;
            }
            if (
              result.some(
                (guessedFile) => guessedFile.mime && accept.types?.includes(guessedFile.mime),
              )
            ) {
              onLoadFile(file_);
            } else {
              errorController('INCORRECT_TYPE');
            }
          } catch {
            errorController('INCORRECT_TYPE');
          }
        } else {
          onLoadFile(file_);
        }
      }
    },
    [fileMaxSize, accept, onLoadFile, errorController],
  );

  const open = useCallback(() => {
    ref.current?.click();
  }, []);
  const remove = useCallback(() => {
    onChangeData();
  }, [onChangeData]);
  const renderValue = useMemo(() => {
    return typeof value === 'string' ? value : value?.base64 || null;
  }, [value]);

  const classNames = clsx(
    'file-uploader',
    value && `file-uploader_active`,
    className,
    state.FileName && 'file',
  );

  return (
    <div className={classNames}>
      <input
        ref={ref}
        onChange={onChangeFile}
        accept={accept.input}
        type="file"
        tabIndex={-1}
        style={{ display: 'none' }}
      />
      {
        render({
          open,
          remove,
          value: renderValue,
          fileName: value_,
          renderProps,
          error,
          validationError,
        }) as React.ReactNode
      }
    </div>
  );
};
